import { useEffect, useRef } from 'react'

import Script from 'next/script'

import { useAuthenticatedPerson } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { useGTMConsentState } from '@b-stock/fe-core/bstock-react'

import { FeatureFlags } from '@/helpers/featureFlags'

type Props = {
  apiKey: string
}

// Note: We can extract this global declaration out in global.d.ts file, if required and update ts-config if required.
declare global {
  interface Window {
    pendo?: {
      initialize?: (options: unknown) => void
      _q: unknown[]
    }
  }
}

/**
 * Pendo Component (Consent-Gated) - Pages Router Version
 *
 * This component loads Pendo's user behavior analytics and in-app guidance system.
 * It requires 'analytics_storage' consent before loading the tracking script.
 *
 * This version uses the external script approach rather than inline JavaScript.
 */
const Pendo = ({ apiKey }: Props) => {
  const shouldInitBraze = useFlag(FeatureFlags.ENABLE_BRAZE_MARKETING_POPUPS)
  const consentState = useGTMConsentState()
  const prevConsentRef = useRef<boolean | null | undefined>(
    consentState.analytics_storage
  )

  const hasConsent = consentState.analytics_storage === true

  // Once this script tag is inserted into the DOM, there is no clean way to stop
  // these user tracking scripts once they have started, so we must force a page
  // reload when consent is withdrawn
  useEffect(() => {
    const currentConsent = consentState.analytics_storage
    const previousConsent = prevConsentRef.current

    // Trigger reload if consent is withdrawn (true -> false)
    if (previousConsent === true && currentConsent === false) {
      console.warn('[Pendo] reload due to withdrawn consent')
      window.location.reload()
    }

    prevConsentRef.current = currentConsent
  }, [consentState.analytics_storage])

  const person = useAuthenticatedPerson()

  useEffect(() => {
    // Only initialize if we have consent
    if (!hasConsent) {
      return
    }

    if (typeof window !== 'undefined') {
      const initArg: Record<string, unknown> = {}

      if (person) {
        initArg.visitor = {
          id: person._id,
          name: `${person.firstName} ${person.lastName}`,
          email: person.email,
        }
        initArg.account = {
          id: person.accountId,
        }
      }

      if (window.pendo?.initialize) {
        // eslint-disable-next-line no-console
        console.debug('[CONSENT] Pendo initializing with user data')
        window.pendo.initialize(initArg)
      } else {
        // eslint-disable-next-line no-console
        console.debug('[CONSENT] Pendo queueing initialization')
        window.pendo = { _q: [['initialize', initArg]] }
      }
    }
  }, [person, hasConsent])

  // Don't render script if Braze is enabled, consent not granted, or GTM not ready
  if (shouldInitBraze || !hasConsent) {
    return null
  }

  return <Script src={`https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`} />
}

export default Pendo
